import {
  DEFAULT_FONT,
  DEFAULT_MONO,
  DEFAULT_PALETTE_ID,
  DEFAULT_SERIF,
  type MonoFontOption,
  type SansFontOption,
  type SerifFontOption,
  resolvePaletteColors,
} from './palettes';

export type ThemeSettings = {
  palette?: string;
  customPalette?: string[];
  typography?: SansFontOption | string;
  typographySerif?: SerifFontOption | string;
  typographyMono?: MonoFontOption | string;
};

const SANS_STACKS: Record<string, string> = {
  Geist: '"Geist", -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
  Roboto: '"Roboto", -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
  System: '-apple-system, BlinkMacSystemFont, system-ui, sans-serif',
};

const SERIF_STACKS: Record<string, string> = {
  'Instrument Serif': '"Instrument Serif", Georgia, serif',
  Merriweather: '"Merriweather", Georgia, "Times New Roman", serif',
  Caveat: '"Caveat", "Comic Sans MS", cursive',
  System: 'Georgia, "Times New Roman", Times, serif',
};

const MONO_STACKS: Record<string, string> = {
  'JetBrains Mono':
    '"JetBrains Mono", "Geist Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
  'Source Code Pro': '"Source Code Pro", ui-monospace, SFMono-Regular, Menlo, monospace',
  'Fira Code': '"Fira Code", ui-monospace, SFMono-Regular, Menlo, monospace',
  'IBM Plex Mono': '"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
  System: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
};

const FALLBACK_SANS = '-apple-system, BlinkMacSystemFont, system-ui, sans-serif';
const FALLBACK_SERIF = 'Georgia, "Times New Roman", Times, serif';
const FALLBACK_MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
const DEFAULT_SANS_STACK = SANS_STACKS[DEFAULT_FONT] ?? FALLBACK_SANS;
const DEFAULT_SERIF_STACK = SERIF_STACKS[DEFAULT_SERIF] ?? FALLBACK_SERIF;
const DEFAULT_MONO_STACK = MONO_STACKS[DEFAULT_MONO] ?? FALLBACK_MONO;

export function applyTheme(settings: ThemeSettings): void {
  const root = document.documentElement;

  const paletteId = settings.palette ?? DEFAULT_PALETTE_ID;
  const colors = resolvePaletteColors(paletteId, settings.customPalette);
  const last = colors[colors.length - 1] ?? '#888';
  for (let i = 0; i < 10; i++) {
    root.style.setProperty(`--c${i}`, colors[i] ?? last);
  }

  const sans = settings.typography ?? DEFAULT_FONT;
  root.style.setProperty('--font-sans', SANS_STACKS[sans] ?? DEFAULT_SANS_STACK);

  const serif = settings.typographySerif ?? DEFAULT_SERIF;
  root.style.setProperty('--font-serif', SERIF_STACKS[serif] ?? DEFAULT_SERIF_STACK);

  const mono = settings.typographyMono ?? DEFAULT_MONO;
  root.style.setProperty('--font-mono', MONO_STACKS[mono] ?? DEFAULT_MONO_STACK);
}

export function readSettingsTheme(
  settings: Record<string, unknown> | null | undefined,
): ThemeSettings {
  if (!settings) return {};
  return {
    palette: typeof settings.palette === 'string' ? settings.palette : undefined,
    customPalette: Array.isArray(settings.customPalette)
      ? (settings.customPalette.filter((x) => typeof x === 'string') as string[])
      : undefined,
    typography: typeof settings.typography === 'string' ? settings.typography : undefined,
    typographySerif:
      typeof settings.typographySerif === 'string' ? settings.typographySerif : undefined,
    typographyMono:
      typeof settings.typographyMono === 'string' ? settings.typographyMono : undefined,
  };
}
